import Loader from '@betterfunnel/ui-core/Loader';
import Select from '@betterfunnel/ui-core/Select';
import { httpsCallable } from 'firebase/functions';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import DashboardAnalyticsRangeSelector from '../../components/DashboardAnalyticsRangeSelector';
import { DashboardContext } from '../../contexts/DashboardContext';
import { UserContext } from '../../contexts/UserContext';
import { functions } from '../../Firebase';
import { analyticsRangeToDates } from '../../utils';

interface StepAnalytics {
  stepId: string;
  stepIndex: number;
  views: number;
  completions: number;
  conversionRate: number;
  averageTimeMs: number;
  dropOffRate: number;
}

interface FormAnalytics {
  formId: string;
  websiteId: string;
  totalViews: number;
  totalCompletions: number;
  completionRate: number;
  averageCompletionTimeMs: number;
  stepAnalytics: StepAnalytics[];
}

interface StepperFormAnalyticsResult {
  totalViews: number;
  totalCompletions: number;
  completionRate: number;
  averageCompletionTimeMs: number;
  formAnalytics: FormAnalytics[];
}

const formatTime = (ms: number): string => {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60000) return `${Math.round(ms / 1000)}s`;
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.round((ms % 60000) / 1000);
  return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
};

const formatPercentage = (value: number): string => {
  return `${value.toFixed(1)}%`;
};

export default function ManageDynamicPageAnalytics() {
  const { selectedDynamicPage, forms, analyticsRange } =
    useContext(DashboardContext);
  const { selectedAccount } = useContext(UserContext);

  const [analytics, setAnalytics] = useState<StepperFormAnalyticsResult | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFormId, setSelectedFormId] = useState<string>('all');

  // Get all unique form IDs from all websites
  const availableForms = useMemo(() => {
    return analytics?.formAnalytics?.map((form) => ({
      formId: form.formId,
      form: forms?.find((f) => f.id === form.formId),
    }));
  }, [analytics, forms]);

  const formOptions = useMemo(() => {
    return [
      { value: 'all', label: 'All Forms' },
      ...(availableForms?.map((form) => ({
        value: form.formId,
        label: `${form.form?.data()?.name || form.formId}`,
      })) || []),
    ];
  }, [availableForms]);

  const fetchAnalytics = useCallback(async () => {
    if (!selectedDynamicPage || !selectedAccount?.id || !analyticsRange) return;

    setLoading(true);
    setError(null);

    try {
      const getStepperFormAnalytics = httpsCallable<
        any,
        StepperFormAnalyticsResult
      >(functions, 'analytics-stepperFormAnalytics');

      const filters: any = {
        campaignId: selectedDynamicPage.id,
      };

      const dateRange = analyticsRangeToDates(analyticsRange);

      const result = await getStepperFormAnalytics({
        accountId: selectedAccount.id,
        filters,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      });

      setAnalytics(result.data);
    } catch (err) {
      console.error('Failed to fetch stepper form analytics:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, [selectedDynamicPage, selectedAccount, analyticsRange]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const filteredAnalytics =
    selectedFormId === 'all'
      ? analytics?.formAnalytics || []
      : analytics?.formAnalytics.filter(
          (form) => form.formId === selectedFormId,
        ) || [];

  return (
    <div className="mt-3 space-y-3">
      {/* Header */}
      <div className="flex items-baseline justify-between">
        <h2 className="text-lg font-semibold text-gray-900">
          Stepper Form Analytics
        </h2>

        <div className="flex gap-4">
          {!!analytics?.formAnalytics.length && !!availableForms?.length && (
            <div className="w-64">
              <Select<string>
                selected={selectedFormId}
                onChange={(value: string) => setSelectedFormId(value)}
                options={formOptions.map((option) => ({
                  value: option.value,
                  child: option.label,
                }))}
                disabled={loading}
              />
            </div>
          )}

          <div className="w-64">
            <DashboardAnalyticsRangeSelector size="normal" disabled={loading} />
          </div>
        </div>
      </div>

      {/* Content Area */}
      {loading && (
        <div className="flex h-64 items-center justify-center">
          <Loader />
          <span className="ml-2 text-gray-600">Loading analytics...</span>
        </div>
      )}

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      {!loading &&
        !error &&
        (!analytics || analytics.formAnalytics.length === 0) && (
          <div className="rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 py-16 text-center">
            <div className="mx-auto max-w-md">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
              <h3 className="mt-4 text-lg font-medium text-gray-900">
                No stepper form data available
              </h3>
              <p className="mt-2 text-sm text-gray-500">
                Stepper forms need to have analytics events to display data
                here.
              </p>
              <div className="mt-6">
                <div className="rounded-md bg-blue-50 p-4">
                  <div className="text-sm text-blue-700">
                    <strong>How to get analytics data:</strong>
                    <ul className="mt-2 list-disc space-y-1 pl-5 text-left">
                      <li>Create stepper forms in your campaigns</li>
                      <li>Ensure forms are published and receiving traffic</li>
                      <li>
                        Analytics will appear once users interact with your
                        forms
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

      {!loading &&
        !error &&
        analytics &&
        analytics.formAnalytics.length > 0 && (
          <>
            {/* Overview Stats */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="text-2xl font-bold text-gray-900">
                  {analytics.totalViews.toLocaleString()}
                </div>
                <div className="text-sm text-gray-600">Total Views</div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="text-2xl font-bold text-green-600">
                  {analytics.totalCompletions.toLocaleString()}
                </div>
                <div className="text-sm text-gray-600">Total Completions</div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="text-2xl font-bold text-blue-600">
                  {formatPercentage(analytics.completionRate)}
                </div>
                <div className="text-sm text-gray-600">Completion Rate</div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="text-2xl font-bold text-purple-600">
                  {formatTime(analytics.averageCompletionTimeMs)}
                </div>
                <div className="text-sm text-gray-600">
                  Avg. Completion Time
                </div>
              </div>
            </div>

            {/* Forms Analytics */}
            {filteredAnalytics.map((formAnalytics) => (
              <div
                key={`${formAnalytics.websiteId}-${formAnalytics.formId}`}
                className="rounded-lg border border-gray-200 bg-white"
              >
                <div className="border-b border-gray-200 px-6 py-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {forms?.find((f) => f.id === formAnalytics.formId)?.data()
                      ?.name || formAnalytics.formId}
                  </h3>
                  <div className="text-sm text-gray-500">
                    {formAnalytics.formId}
                  </div>

                  {(availableForms?.length ?? 0) > 1 && (
                    <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-4">
                      <div>
                        <div className="text-xl font-bold text-gray-900">
                          {formAnalytics.totalViews.toLocaleString()}
                        </div>
                        <div className="text-xs text-gray-600">Views</div>
                      </div>
                      <div>
                        <div className="text-xl font-bold text-green-600">
                          {formAnalytics.totalCompletions.toLocaleString()}
                        </div>
                        <div className="text-xs text-gray-600">Completions</div>
                      </div>
                      <div>
                        <div className="text-xl font-bold text-blue-600">
                          {formatPercentage(formAnalytics.completionRate)}
                        </div>
                        <div className="text-xs text-gray-600">
                          Completion Rate
                        </div>
                      </div>
                      <div>
                        <div className="text-xl font-bold text-purple-600">
                          {formatTime(formAnalytics.averageCompletionTimeMs)}
                        </div>
                        <div className="text-xs text-gray-600">Avg. Time</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Step Analytics Table */}
                <div className="py-6">
                  <h4 className="text-md mb-4 px-6 font-medium text-gray-900">
                    Step-by-Step Analysis
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 px-6">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                            Step
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                            Views
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                            Completions
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                            Conversion Rate
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                            Avg. Time
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                            Drop-off Rate
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {formAnalytics.stepAnalytics.map((step) => (
                          <tr key={step.stepId} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {step.stepId}
                              </div>
                              <div className="text-sm text-gray-500">
                                Step {step.stepIndex + 1}
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm whitespace-nowrap text-gray-900">
                              {step.views.toLocaleString()}
                            </td>
                            <td className="px-6 py-4 text-sm font-medium whitespace-nowrap text-green-600">
                              {step.completions.toLocaleString()}
                            </td>
                            <td className="px-6 py-4 text-sm font-medium whitespace-nowrap text-blue-600">
                              {formatPercentage(step.conversionRate)}
                            </td>
                            <td className="px-6 py-4 text-sm font-medium whitespace-nowrap text-purple-600">
                              {formatTime(step.averageTimeMs)}
                            </td>
                            <td className="px-6 py-4 text-sm font-medium whitespace-nowrap text-red-600">
                              {formatPercentage(step.dropOffRate)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ))}
          </>
        )}
    </div>
  );
}
